import { formatDateNumber } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import {
  ExtendedMedicationRequest,
  medicationLabelForGroup,
} from '@seen/core/models/medicationRequest'
import { VisitNote } from '@seen/core/models/visitNote'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { Badge, cn, DotSeparator } from '@seen/ui'
import type { JSX } from 'react'
import { useMemo } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { MedicationHistoryTooltip } from '#src/components/medications/MedicationHistoryTooltip.tsx'
import { MedicationName } from '#src/components/medications/MedicationName.tsx'
import { useFeatureFlag, useSearch } from '#src/hooks/index.ts'

interface VisitNoteMedicationsWidgetProps {
  visitNote: VisitNote
  patientId: string
}

export function VisitNoteMedicationsWidget({
  visitNote,
  patientId,
}: VisitNoteMedicationsWidgetProps): JSX.Element | null {
  const medicationRequestIdString = useMemo(() => {
    const orderEntries = Object.values(visitNote.sections)
      .flat()
      .filter((s) => VisitNote.isOrderSection(s))
      .flatMap((s) => s.entry ?? [])
    return orderEntries
      .filter((e) => isOfType(e, 'MedicationRequest'))
      .map((r) => resolveId(r))
      .filter(Boolean)
      .join(',')
  }, [visitNote])

  const medicationRequestIds = useMemo(
    () =>
      medicationRequestIdString ? medicationRequestIdString.split(',') : [],
    [medicationRequestIdString]
  )

  const { data: linkedMeds, isLoading: linkedMedsLoading } = useSearch(
    ExtendedMedicationRequest,
    medicationRequestIds.length > 0
      ? ExtendedMedicationRequest.queryByIds(medicationRequestIds)
      : undefined
  )

  const { data: allMeds, isLoading: allMedsLoading } = useSearch(
    ExtendedMedicationRequest,
    medicationRequestIds.length > 0
      ? ExtendedMedicationRequest.queryAllBySubject(`Patient/${patientId}`, {
          includeDraft: true,
        })
      : undefined
  )

  const groupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )

  const { medicationGroups, linkedMedIds } = useMemo(() => {
    if (!linkedMeds?.length || !allMeds?.length) {
      return { medicationGroups: [], linkedMedIds: new Set<string>() }
    }
    const ids = new Set(
      linkedMeds.map((m) => m.id).filter((id): id is string => !!id)
    )
    const allGroups = ExtendedMedicationRequest.groupMedications(allMeds, {
      groupByElationThread,
    })
    return {
      medicationGroups: allGroups.filter((group) =>
        group.requests.some((req) => ids.has(req.id ?? ''))
      ),
      linkedMedIds: ids,
    }
  }, [linkedMeds, allMeds, groupByElationThread])

  if (medicationRequestIds.length === 0) {
    return null
  }

  const medsLoading = linkedMedsLoading || allMedsLoading
  const hasMeds = medicationGroups.length > 0

  if (!medsLoading && !hasMeds) {
    return null
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <h4 className="text-sm font-medium">Medications</h4>
        {hasMeds && (
          <span className="text-xs text-muted-foreground">
            {medicationGroups.length}
          </span>
        )}
      </div>
      {medsLoading ? (
        <div className="px-3 py-4 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : (
        <div className="flex flex-col gap-1 px-3 py-2 text-sm">
          {medicationGroups.map((group, idx) => {
            const groupLabel = medicationLabelForGroup(group)
            const actionRequest = group.representativeRequest
            const date = actionRequest.authoredOn
            const state = ExtendedMedicationRequest.classifyMedicationState([
              actionRequest.toFhirResource(),
            ])
            const label = (
              <MedicationName
                display={groupLabel.display}
                doseSummary={groupLabel.doseSummary}
              />
            )
            return (
              <HoverCard
                key={actionRequest.id ?? `${group.medicationName}-${idx}`}
                openDelay={200}
                closeDelay={100}
              >
                <HoverCardTrigger asChild>
                  <div>
                    {actionRequest.id ? (
                      <TextLink
                        to={`/Patient/${patientId}/medications/${actionRequest.id}`}
                      >
                        {label}
                      </TextLink>
                    ) : (
                      <span>{label}</span>
                    )}
                    {date && (
                      <span className="select-none">
                        <DotSeparator />
                        <span className="text-foreground-tertiary">
                          {formatDateNumber(date)}
                        </span>
                      </span>
                    )}
                    {(state.category === 'active-expired' ||
                      state.category === 'active-outdated' ||
                      !state.category.startsWith('active-')) && (
                      <MedStatusBadge
                        category={state.category}
                        label={state.label}
                      />
                    )}
                  </div>
                </HoverCardTrigger>
                <HoverCardContent
                  side="left"
                  align="start"
                  alignOffset={-16}
                  className="w-auto"
                >
                  <MedicationHistoryTooltip
                    medicationDisplay={label}
                    medicationRequests={group.requests}
                    actionRequestId={actionRequest.id}
                    highlightedRequestIds={linkedMedIds}
                  />
                </HoverCardContent>
              </HoverCard>
            )
          })}
        </div>
      )}
    </div>
  )
}

type MedStateCategory = ReturnType<
  typeof ExtendedMedicationRequest.classifyMedicationState
>['category']

const STATUS_VARIANT: Record<
  MedStateCategory,
  'default' | 'secondary' | 'outline'
> = {
  'active-current': 'default',
  'active-likely-finished': 'default',
  'active-expired': 'outline',
  'active-outdated': 'outline',
  stopped: 'secondary',
  cancelled: 'secondary',
  completed: 'secondary',
  unknown: 'outline',
}

const STATUS_COLOR: Record<
  MedStateCategory,
  'green' | 'gray' | 'red' | 'orange' | undefined
> = {
  'active-current': 'green',
  'active-likely-finished': 'green',
  'active-expired': 'red',
  'active-outdated': 'orange',
  stopped: 'gray',
  cancelled: 'gray',
  completed: 'gray',
  unknown: undefined,
}

function MedStatusBadge({
  category,
  label,
}: {
  category: MedStateCategory
  label: string
}): JSX.Element {
  const displayLabel = category.startsWith('active-') ? 'Active' : label
  return (
    <span className="ml-1.5 select-none">
      <Badge
        variant={STATUS_VARIANT[category]}
        color={STATUS_COLOR[category]}
        data-medication-state={category}
        className={cn(
          'inline-flex shrink-0 items-center',
          STATUS_VARIANT[category] !== 'outline' &&
            'h-4 px-1.5 py-0 text-[10px]'
        )}
      >
        {displayLabel}
      </Badge>
    </span>
  )
}
